import { toast as sonnerToast } from 'sonner'

/**
 * Toast notification utilities
 * Provides convenient wrapper functions for different types of notifications
 */

// Validation toasts (success/error/warning/info) all share this ID so each
// new one instantly replaces the previous — no stacking, no queue buildup.
const VALIDATION_TOAST_ID = 'validation'

export const toast = {
  /**
   * Show a success notification (replaces any existing validation toast)
   */
  success: (message: string, description?: string) => {
    return sonnerToast.success(message, {
      id: VALIDATION_TOAST_ID,
      description: description ?? undefined,
    })
  },

  /**
   * Show an error notification (replaces any existing validation toast)
   */
  error: (message: string, description?: string, options?: Parameters<typeof sonnerToast>[1]) => {
    return sonnerToast.error(message, {
      id: VALIDATION_TOAST_ID,
      description: description ?? undefined,
      ...options,
    })
  },

  /**
   * Show a warning notification (replaces any existing validation toast)
   */
  warning: (message: string, description?: string) => {
    return sonnerToast.warning(message, {
      id: VALIDATION_TOAST_ID,
      description: description ?? undefined,
    })
  },

  /**
   * Show an info notification (replaces any existing validation toast)
   */
  info: (message: string, description?: string) => {
    return sonnerToast.info(message, {
      id: VALIDATION_TOAST_ID,
      description: description ?? undefined,
    })
  },

  /**
   * Show a loading notification
   * Returns a toast ID that can be used to dismiss or update the toast
   */
  loading: (message: string, description?: string) => {
    return sonnerToast.loading(message, description ? { description } : undefined)
  },

  /**
   * Show a promise notification
   * Automatically shows loading, success, or error based on promise result
   */
  promise: <T,>(
    promise: Promise<T>,
    options: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: Error) => string)
    }
  ) => {
    return sonnerToast.promise(promise, options)
  },

  /**
   * Dismiss a specific toast by ID
   */
  dismiss: (toastId?: string | number) => {
    sonnerToast.dismiss(toastId)
  },

  /**
   * Custom toast with manual control
   */
  custom: (message: string, options?: Parameters<typeof sonnerToast>[1]) => {
    return sonnerToast(message, options)
  },
}

// Example usage:
// toast.success('Operation successful!', 'Your changes have been saved.')
// toast.error('Something went wrong', 'Please try again later.')
// toast.warning('Warning!', 'This action cannot be undone.')
// toast.info('Did you know?', 'You can use keyboard shortcuts.')
//
// const loadingToast = toast.loading('Processing...', 'Please wait')
// // Later: toast.dismiss(loadingToast)
//
// toast.promise(
//   fetch('/api/data'),
//   {
//     loading: 'Loading data...',
//     success: 'Data loaded!',
//     error: 'Failed to load data',
//   }
// )
